import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, FileText, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { toast } from 'sonner';
import { customPagesAPI } from '../../services/api';
import { RichTextEditor } from '../ui/rich-text-editor';
import { ImageUpload } from '../ui/image-upload';

// Predefined pages that can be managed
const predefinedPages = [
  { slug: 'condizioni-vendita', title: 'Condizioni di Vendita', description: 'Termini e condizioni di vendita' },
  { slug: 'privacy-candidati', title: 'Privacy Candidati', description: 'Informativa privacy per candidati' },
];

const CustomPagesTab = () => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [pageForm, setPageForm] = useState({ slug: '', title: '', content: '', pdf_url: '' });

  useEffect(() => {
    loadPages();
  }, []);

  const loadPages = async () => {
    try {
      const response = await customPagesAPI.getAll();
      setPages(response.data);
    } catch (error) {
      // No pages yet, that's ok
      setPages([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!pageForm.slug || !pageForm.title) {
      toast.error('Slug e titolo sono obbligatori');
      return;
    }

    try {
      if (editingPage) {
        await customPagesAPI.update(pageForm.slug, pageForm);
        toast.success('Pagina aggiornata!');
      } else {
        await customPagesAPI.create(pageForm);
        toast.success('Pagina creata!');
      }
      loadPages();
      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(
        error.response?.status === 401
          ? 'Sessione scaduta: accedi di nuovo e riprova a salvare'
          : error.response?.data?.detail || 'Errore nel salvare la pagina'
      );
    }
  };

  const handleDelete = async (slug) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa pagina?')) return;
    
    try {
      await customPagesAPI.remove(slug);
      toast.success('Pagina eliminata!');
      loadPages();
    } catch (error) {
      toast.error('Errore nell\'eliminare la pagina');
    }
  };

  const handleEdit = (page) => {
    setEditingPage(page);
    setPageForm({
      slug: page.slug,
      title: page.title,
      content: page.content || '',
      pdf_url: page.pdf_url || ''
    });
    setIsDialogOpen(true);
  };

  const handleCreatePredefined = (predefined) => {
    setEditingPage(null);
    setPageForm({
      slug: predefined.slug,
      title: predefined.title,
      content: '',
      pdf_url: ''
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingPage(null);
    setPageForm({ slug: '', title: '', content: '', pdf_url: '' });
  };

  const getPageBySlug = (slug) => pages.find(p => p.slug === slug);

  return (
    <Card className="glass-card">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between space-y-4 sm:space-y-0">
          <div>
            <CardTitle className="text-2xl">Pagine Personalizzate</CardTitle>
            <CardDescription>Gestisci le pagine legali e informative del sito</CardDescription>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={resetForm} data-testid="add-page-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nuova Pagina
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingPage ? 'Modifica Pagina' : 'Nuova Pagina'}</DialogTitle>
                <DialogDescription>
                  {editingPage ? 'Modifica il contenuto della pagina' : 'Crea una nuova pagina personalizzata'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="slug">Slug (URL)</Label>
                    <Input
                      id="slug"
                      value={pageForm.slug}
                      onChange={(e) => setPageForm({ ...pageForm, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                      placeholder="es. condizioni-vendita"
                      className="mt-2 font-mono"
                      disabled={!!editingPage}
                      required
                      data-testid="page-slug-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      URL: /{pageForm.slug || 'slug-pagina'}
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="title">Titolo</Label>
                    <Input
                      id="title"
                      value={pageForm.title}
                      onChange={(e) => setPageForm({ ...pageForm, title: e.target.value })}
                      placeholder="Titolo della pagina"
                      className="mt-2"
                      required
                      data-testid="page-title-input"
                    />
                  </div>
                </div>

                <div>
                  <Label>Documento PDF (opzionale)</Label>
                  <div className="mt-2">
                    <ImageUpload
                      value={pageForm.pdf_url}
                      onChange={(url) => setPageForm({ ...pageForm, pdf_url: url })}
                      uploadType="document"
                      label="Carica PDF"
                      accept=".pdf"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Il PDF sarà disponibile per il download nella pagina
                  </p>
                </div>

                <div>
                  <Label>Contenuto</Label>
                  <div className="mt-2">
                    <RichTextEditor
                      value={pageForm.content}
                      onChange={(html) => setPageForm({ ...pageForm, content: html })}
                      placeholder="Scrivi il contenuto della pagina..."
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Annulla
                  </Button>
                  <Button type="submit" data-testid="submit-page-btn">
                    {editingPage ? 'Aggiorna' : 'Crea'} Pagina
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {/* Predefined Pages Quick Actions */}
        <div className="mb-6">
          <h3 className="text-sm font-medium mb-3 text-muted-foreground">Pagine Predefinite</h3>
          <div className="grid md:grid-cols-2 gap-3">
            {predefinedPages.map((predefined) => {
              const existingPage = getPageBySlug(predefined.slug);
              return (
                <div 
                  key={predefined.slug}
                  className="flex items-center justify-between p-4 rounded-lg border border-border bg-muted/30"
                >
                  <div>
                    <p className="font-medium">{predefined.title}</p>
                    <p className="text-sm text-muted-foreground">{predefined.description}</p>
                  </div>
                  {existingPage ? (
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => handleEdit(existingPage)}>
                        <Edit className="w-4 h-4 mr-1" />
                        Modifica
                      </Button>
                      <Button variant="ghost" size="sm" asChild>
                        <a href={`/${predefined.slug}`} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </Button>
                    </div>
                  ) : (
                    <Button size="sm" onClick={() => handleCreatePredefined(predefined)}>
                      <Plus className="w-4 h-4 mr-1" />
                      Crea
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* All Pages Table */}
        <div className="rounded-lg border border-border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Titolo</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead>PDF</TableHead>
                <TableHead>Ultimo Aggiornamento</TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">Caricamento...</TableCell>
                </TableRow>
              ) : pages.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                    Nessuna pagina personalizzata. Usa i pulsanti sopra per creare le pagine predefinite.
                  </TableCell>
                </TableRow>
              ) : (
                pages.map((page) => (
                  <TableRow key={page.id}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-muted-foreground" />
                        {page.title}
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-sm">/{page.slug}</TableCell>
                    <TableCell>
                      {page.pdf_url ? (
                        <a href={page.pdf_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline text-sm">
                          Scarica
                        </a>
                      ) : (
                        <span className="text-muted-foreground text-sm">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {new Date(page.updated_at).toLocaleDateString('it-IT')}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end space-x-2">
                        <Button variant="ghost" size="icon" asChild>
                          <a href={`/${page.slug}`} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(page)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => handleDelete(page.slug)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

export default CustomPagesTab;
